import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewContainerRef
} from "@angular/core";
import { merge, Subject, Subscription } from "rxjs";
import { takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { ControlValueAccessorBaseComponent } from "../components/base-component/control-value-accessor-base.component";
import { IValidationRule } from "./IValidationRule";
import { ValidationResult } from "./ValidationResult";
import { ValidationRuleController } from "./ValidationRuleController";
import { ValidationService } from "./validation.service";

export type ValidationRuleDisplayType = "Container" | "Popover";

@Directive({ selector: "[raskRule]" })
export class ValidationRuleDirective implements AfterViewInit, OnChanges, OnDestroy {
    @HostBinding("class.validation-error") highlightError = false;
    @Input() raskRuleDisplayType: ValidationRuleDisplayType = "Container";
    @Input("raskRule") rule: ValidationRuleController;
    @Input() ruleContainerId: string;
    @Input() ruleRowDepth: number;

    private readonly elementRef = inject(ElementRef);
    private readonly host = inject(ControlValueAccessorBaseComponent);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly onInit$ = new Subject<void>();
    private readonly validationService = inject(ValidationService);
    private readonly viewContainerRef = inject(ViewContainerRef);

    private rulesSubscription: Subscription;

    ngAfterViewInit(): void {
        this.onInit$.next();
        if (!this.rule) {
            return;
        }

        this.validateOnLoad();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.rule) {
            return;
        }

        this.host.beforeChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(p => {
            this.validate(p.oldValue, p.newValue);
        });

        this.rulesSubscription?.unsubscribe();
        this.rulesSubscription = merge(...this.rule.rules.map(r => r.changed.pipe(
            withLatestFrom(this.onInit$, (res, _) => res),
            tap((res) => this.syncValidation(r, res)),
            takeUntil(this.ngUnsubscribe$)
        ))).subscribe();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private clearMessages(): void {
        this.highlightError = false;
        this.rule.rules.forEach(r => {
            this.validationService.removeMessageForRule(r, this.elementRef.nativeElement, this.raskRuleDisplayType);
        });
    }

    private showMessage(rule: IValidationRule): void {
        this.validationService.showMessageForRule(
            this.viewContainerRef,
            this.elementRef.nativeElement,
            rule, this.raskRuleDisplayType,
            this.ruleRowDepth,
            this.ruleContainerId);
    }

    private syncValidation(rule: IValidationRule, validationResult: ValidationResult) {
        // Filtrera bort valideringars som körs på samma regel fast från andra element (i tabeller tex)
        if (validationResult.caller && validationResult.caller !== this.elementRef) {
            return;
        }

        this.clearMessages();
        this.highlightError = false;

        // Triggern på rule sker innan, error / warning flagga har satts så vi får filtrera själva.
        const invalid = this.rule.rules.filter(r => (r.hasError || r.hasWarning) && r.name !== rule.name);
        if (!validationResult.result) {
            invalid.push(rule);
        }

        invalid.forEach(r => {
            this.showMessage(r);
            this.highlightError = true;
        });
    }

    private validate(oldValue, newValue): void {
        this.clearMessages();
        if (!this.rule) {
            return;
        }

        this.rule.validateValue(oldValue, newValue, this.elementRef);
    }

    private validateOnLoad(): void {
        requestAnimationFrame(() => {
            if (this.rule && this.rule.rules.some(r => r.settings.triggerOnLoad)) {
                this.validate(this.host.value, this.host.value);
            }
        });
    }
}
